// Reusable guest-site section building blocks for the GuestPage variants.
// Every variant (editorial, magazine, ceremonial, …) composes these
// sections — countdown, story, gallery, schedule, map, RSVP, guestbook,
// share, footer — so keep them visually consistent and prop-stable.
// Ported from legacy-mockup/sections.jsx with proper TypeScript types.

import { useState, useEffect, useRef, useCallback } from "react";
import type { CSSProperties, Dispatch, SetStateAction } from "react";
import type { Template } from "@/lib/data";
import * as Ornaments from "@/components/Ornaments";
import { postRSVP, postGuestbook, getPublicGuestbook } from "@/lib/api";
import { useT } from "@/lib/i18n";

// ─── shared helpers ────────────────────────────────────────────────────

interface GuestPhotoProps {
  src?: string;
  style?: CSSProperties;
  filter?: string;
  fallbackTone?: string;
  fallbackBg?: string;
  onClick?: () => void;
}

export function GuestPhoto({ src, style, filter, fallbackTone, fallbackBg, onClick }: GuestPhotoProps) {
  if (src) {
    return (
      <div
        onClick={onClick}
        style={{
          ...style,
          backgroundImage: `url("${src}")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: filter || "none",
          cursor: onClick ? "zoom-in" : style?.cursor,
        }}
      />
    );
  }
  return (
    <div
      onClick={onClick}
      style={{
        ...style,
        background: `linear-gradient(135deg, ${fallbackBg}, ${fallbackTone}33)`,
        filter: filter || "none",
        cursor: onClick ? "zoom-in" : style?.cursor,
      }}
    />
  );
}

// ─── event-closed cutoff ───────────────────────────────────────────────
// The guestbook stops accepting new wishes once 24h have passed since the
// wedding start (date + time). Reading the list stays open forever — the
// closed state only hides the submit form. Used by GuestbookSection.
// `time` falls back to 17:00 (the same default the countdown uses) when
// the invitation hasn't set one.
function isPastEvent(date?: string, time?: string): boolean {
  if (!date) return false;
  const [dd, mm, yyyy] = date.split(".").map((p) => parseInt(p, 10));
  if (!dd || !mm || !yyyy) return false;
  const [hh, mi] = (time ?? "17:00").split(":").map((p) => parseInt(p, 10));
  const start = new Date(yyyy, mm - 1, dd, hh || 17, mi || 0, 0);
  if (Number.isNaN(start.getTime())) return false;
  return Date.now() >= start.getTime() + 24 * 60 * 60 * 1000;
}

// ─── prefers-reduced-motion ────────────────────────────────────────────

function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduce(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);
  return reduce;
}

// ─── ICS helper (inline fallback) ──────────────────────────────────────
// The shared @/lib/ics module is being shipped by a parallel agent. To stay
// compilable & functional today, we inline a tiny implementation. When the
// shared module lands, callers can drop this in favour of `import { downloadICS }
// from "@/lib/ics"` without breaking the API.

interface ICSEvent {
  title: string;
  description?: string;
  location?: string;
  /** "DD.MM.YYYY" (matches Template.date) */
  date: string;
  /** "HH:MM" 24h */
  time?: string;
  durationHours?: number;
  url?: string;
}

function pad(n: number) { return String(n).padStart(2, "0"); }

function formatICSDate(d: Date): string {
  return (
    d.getUTCFullYear().toString() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    "T" +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    "Z"
  );
}

function escapeICS(s: string): string {
  return s.replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");
}

function downloadICS(ev: ICSEvent) {
  try {
    const [dd, mm, yyyy] = ev.date.split(".").map((p) => parseInt(p, 10));
    const [hh, mi] = (ev.time ?? "17:00").split(":").map((p) => parseInt(p, 10));
    if (!dd || !mm || !yyyy) {
      console.warn("ICS: bad date", ev.date);
      return;
    }
    const start = new Date(yyyy, (mm || 1) - 1, dd, hh || 17, mi || 0);
    const end = new Date(start.getTime() + (ev.durationHours ?? 4) * 60 * 60 * 1000);
    const uid = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}@taklifla.uz`;
    const lines = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//taklifla.uz//EN",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "BEGIN:VEVENT",
      `UID:${uid}`,
      `DTSTAMP:${formatICSDate(new Date())}`,
      `DTSTART:${formatICSDate(start)}`,
      `DTEND:${formatICSDate(end)}`,
      `SUMMARY:${escapeICS(ev.title)}`,
      ev.description ? `DESCRIPTION:${escapeICS(ev.description)}` : null,
      ev.location ? `LOCATION:${escapeICS(ev.location)}` : null,
      ev.url ? `URL:${ev.url}` : null,
      "END:VEVENT",
      "END:VCALENDAR",
    ].filter(Boolean) as string[];
    const blob = new Blob([lines.join("\r\n")], { type: "text/calendar;charset=utf-8" });
    const a = document.createElement("a");
    const href = URL.createObjectURL(blob);
    a.href = href;
    a.download = (ev.title || "event").replace(/[^a-z0-9-_]+/gi, "_") + ".ics";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(href), 1000);
  } catch (e) {
    console.warn("ICS coming soon", e);
  }
}

// ─── COUNTDOWN ─────────────────────────────────────────────────────────

interface CountdownSectionProps {
  t: Template;
  days: number;
  hours: number;
  mins: number;
  secs: number;
  isDark: boolean;
  /** True when the target time is in the past — render an "event passed" line
      instead of four zero panels. Optional so older callers still type-check. */
  passed?: boolean;
}

export function CountdownSection({ t, days, hours, mins, secs, isDark, passed }: CountdownSectionProps) {
  const tr = useT();
  const cells: Array<[number, string]> = [
    [days, tr("sections.countdown.day")],
    [hours, tr("sections.countdown.hour")],
    [mins, tr("sections.countdown.minute")],
    [secs, tr("sections.countdown.second")],
  ];
  return (
    <section
      style={{
        padding: "80px 48px",
        borderTop: `1px solid ${t.ink}22`,
        background: isDark ? `${t.ink}33` : `${t.accent}15`,
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.4em",
          color: t.accent,
          marginBottom: 16,
        }}
      >
        {tr("sections.countdown.eyebrow")}
      </div>
      {passed ? (
        <div
          style={{
            marginTop: 40,
            padding: "32px 28px",
            display: "inline-block",
            border: `1px solid ${t.accent}`,
            background: t.bg,
            fontFamily: "var(--font-ui)",
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: t.accent,
            textTransform: "uppercase",
          }}
        >
          {tr("sections.countdown.passed", "Marosim o'tdi")}
        </div>
      ) : (
      <div style={{ display: "flex", justifyContent: "center", gap: 24, marginTop: 40, flexWrap: "wrap" }}>
        {cells.map(([n, l]) => (
          <div
            key={l}
            style={{
              minWidth: 140,
              padding: "32px 20px",
              background: t.bg,
              border: `1px solid ${t.accent}`,
              position: "relative",
              boxShadow: `6px 7px 0 -1px ${t.accent}33`,
            }}
          >
            <div
              style={{
                fontFamily: t.font || "var(--font-display)",
                fontStyle: "italic",
                fontSize: 72,
                fontWeight: 300,
                color: t.accent,
                lineHeight: 1,
              }}
            >
              {String(n).padStart(2, "0")}
            </div>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.2em",
                marginTop: 12,
              }}
            >
              {l}
            </div>
          </div>
        ))}
      </div>
      )}
    </section>
  );
}

// ─── STORY ─────────────────────────────────────────────────────────────

interface StorySectionProps {
  t: Template;
  title: string;
  body: string;
  eyebrow?: string;
}

export function StorySection({ t, title, body, eyebrow }: StorySectionProps) {
  return (
    <section style={{ padding: "120px 48px", borderTop: `1px solid ${t.ink}22`, textAlign: "center" }}>
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        {eyebrow && (
          <div
            style={{
              fontFamily: "var(--font-hand)",
              fontSize: 32,
              color: t.accent,
              marginBottom: 24,
            }}
          >
            {eyebrow}
          </div>
        )}
        <h2
          style={{
            fontFamily: t.font || "var(--font-display)",
            fontSize: "clamp(36px, 5vw, 56px)",
            fontStyle: "italic",
            fontWeight: 400,
            marginBottom: 32,
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </h2>
        <p
          style={{
            fontFamily: "var(--font-body)",
            fontSize: 19,
            lineHeight: 1.7,
            fontStyle: "italic",
            color: `${t.ink}cc`,
          }}
        >
          {body}
        </p>
      </div>
    </section>
  );
}

// ─── LIGHTBOX (mobile-friendly) ────────────────────────────────────────

interface LightboxProps {
  photos: string[];
  idx: number;
  onClose: () => void;
  onNav: (next: number) => void;
}

function Lightbox({ photos, idx, onClose, onNav }: LightboxProps) {
  const tr = useT();
  const reduce = usePrefersReducedMotion();
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const touchMoved = useRef<boolean>(false);

  // ESC close + body scroll lock
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onNav((idx - 1 + photos.length) % photos.length);
      else if (e.key === "ArrowRight") onNav((idx + 1) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [idx, photos.length, onClose, onNav]);

  const onTouchStart = (e: React.TouchEvent) => {
    const t0 = e.touches[0];
    touchStartX.current = t0.clientX;
    touchStartY.current = t0.clientY;
    touchMoved.current = false;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    const t0 = e.touches[0];
    const dx = Math.abs(t0.clientX - touchStartX.current);
    const dy = Math.abs(t0.clientY - (touchStartY.current ?? t0.clientY));
    if (dx > 5 || dy > 5) touchMoved.current = true;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    const endX = e.changedTouches[0].clientX;
    const dx = endX - touchStartX.current;
    if (touchMoved.current && Math.abs(dx) > 50) {
      if (dx < 0) onNav((idx + 1) % photos.length);
      else onNav((idx - 1 + photos.length) % photos.length);
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  // Tap left half = prev, right half = next (when no swipe).
  const onClickArea = (e: React.MouseEvent<HTMLDivElement>) => {
    if (touchMoved.current) return; // swipe handled
    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    const xRel = e.clientX - rect.left;
    if (xRel < rect.width * 0.35) onNav((idx - 1 + photos.length) % photos.length);
    else if (xRel > rect.width * 0.65) onNav((idx + 1) % photos.length);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 999,
        background: "rgba(0,0,0,0.92)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        animation: reduce ? "none" : "fadeIn .15s ease",
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onClick={onClickArea}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label={tr("sections.lightbox.close")}
        style={{
          position: "absolute",
          top: 16,
          right: 16,
          width: 44,
          height: 44,
          background: "rgba(0,0,0,0.5)",
          border: "1px solid rgba(255,255,255,0.4)",
          color: "#fff",
          fontSize: 22,
          cursor: "pointer",
          zIndex: 2,
        }}
      >
        ×
      </button>
      <div
        style={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
          boxSizing: "border-box",
        }}
      >
        <img
          src={photos[idx]}
          alt=""
          draggable={false}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            userSelect: "none",
          }}
        />
      </div>
      {photos.length > 1 && (
        <div
          style={{
            position: "absolute",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            color: "rgba(255,255,255,0.85)",
            fontFamily: "var(--font-ui)",
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.2em",
            background: "rgba(0,0,0,0.4)",
            padding: "6px 14px",
            border: "1px solid rgba(255,255,255,0.2)",
          }}
        >
          {idx + 1} / {photos.length}
        </div>
      )}
    </div>
  );
}

// ─── GALLERY (multi-photo carousel) ────────────────────────────────────

interface GallerySectionProps {
  t: Template;
}

export function GallerySection({ t }: GallerySectionProps) {
  const tr = useT();
  const [idx, setIdx] = useState(0);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const photos = t.photos && t.photos.length ? t.photos : [];
  if (!photos.length) return null;
  return (
    <section className="cv-auto" style={{ padding: "100px 48px", borderTop: `1px solid ${t.ink}22` }}>
      {/* Mobile gallery rule keyed on a STABLE class — the global
          mobile-lockdown only stacks grids two levels deep inside a
          <section>, and several variants nest the gallery deeper, which
          left the 3-column grid intact and squeezed the centre photo to
          ~177px ("small, old-UI" photos). On phones we collapse to one
          full-width photo and drop the two decorative side panels. */}
      <style>{`
        @media (max-width: 768px) {
          .tk-gallery-grid {
            grid-template-columns: 1fr !important;
            max-width: 460px !important;
          }
          .tk-gallery-side { display: none !important; }
        }
      `}</style>
      <div style={{ textAlign: "center", marginBottom: 56 }}>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: t.accent,
            marginBottom: 16,
          }}
        >
          {tr("sections.gallery.eyebrow")}
        </div>
        <h2
          style={{
            fontFamily: t.font || "var(--font-display)",
            fontSize: 48,
            fontStyle: "italic",
            fontWeight: 400,
          }}
        >
          {tr("sections.gallery.title")}
        </h2>
      </div>
      <div
        className="tk-gallery-grid"
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1fr 2fr 1fr",
          gap: 24,
          alignItems: "center",
        }}
      >
        <div
          className="tk-gallery-side"
          style={{ aspectRatio: "3/4", opacity: 0.6, transform: "translateY(20px)", overflow: "hidden" }}
        >
          <GuestPhoto
            src={photos[(idx + photos.length - 1) % photos.length]}
            style={{ width: "100%", height: "100%" }}
          />
        </div>
        <div style={{ aspectRatio: "4/5", position: "relative", overflow: "hidden" }}>
          <GuestPhoto
            src={photos[idx]}
            style={{ width: "100%", height: "100%" }}
            onClick={() => setLightboxIdx(idx)}
          />
          <div
            style={{
              position: "absolute",
              bottom: -1,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              gap: 8,
              padding: "12px 20px",
              background: t.bg,
              border: `1px solid ${t.accent}`,
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIdx((idx - 1 + photos.length) % photos.length);
              }}
              style={{
                width: 32,
                height: 32,
                border: "none",
                background: "transparent",
                color: t.ink,
                cursor: "pointer",
              }}
            >
              ←
            </button>
            <span
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 600,
                padding: "8px 12px",
                color: t.accent,
              }}
            >
              {idx + 1} / {photos.length}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIdx((idx + 1) % photos.length);
              }}
              style={{
                width: 32,
                height: 32,
                border: "none",
                background: "transparent",
                color: t.ink,
                cursor: "pointer",
              }}
            >
              →
            </button>
          </div>
        </div>
        <div
          className="tk-gallery-side"
          style={{ aspectRatio: "3/4", opacity: 0.6, transform: "translateY(-20px)", overflow: "hidden" }}
        >
          <GuestPhoto src={photos[(idx + 1) % photos.length]} style={{ width: "100%", height: "100%" }} />
        </div>
      </div>
      {lightboxIdx !== null && (
        <Lightbox
          photos={photos}
          idx={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onNav={(n) => {
            setLightboxIdx(n);
            setIdx(n);
          }}
        />
      )}
    </section>
  );
}

// ─── SCHEDULE ──────────────────────────────────────────────────────────

interface ScheduleSectionProps {
  t: Template;
  items: Array<[string, string]>;
}

export function ScheduleSection({ t, items }: ScheduleSectionProps) {
  const tr = useT();
  const handleICS = useCallback(() => {
    const suffix =
      t.category === "wedding"
        ? tr("sections.ics.suffix.wedding")
        : t.category === "birthday"
          ? tr("sections.ics.suffix.birthday")
          : "";
    const description =
      t.category === "wedding"
        ? tr("sections.ics.description.wedding")
        : tr("sections.ics.description.default");
    downloadICS({
      title: t.names + suffix,
      description,
      location: t.venue,
      date: t.date,
      time: items[0]?.[0]?.match(/^\d{1,2}:\d{2}/)?.[0] || "17:00",
      durationHours: 4,
      url: typeof window !== "undefined" ? window.location.href : undefined,
    });
  }, [t.category, t.names, t.venue, t.date, items, tr]);

  return (
    <section style={{ padding: "100px 48px", borderTop: `1px solid ${t.ink}22` }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <div style={{ position: "relative", textAlign: "center", marginBottom: 56 }}>
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.4em",
              color: t.accent,
              marginBottom: 16,
            }}
          >
            {tr("sections.schedule.eyebrow")}
          </div>
          <h2
            style={{
              fontFamily: t.font || "var(--font-display)",
              fontSize: 48,
              fontStyle: "italic",
              fontWeight: 400,
            }}
          >
            {tr("sections.schedule.title")}
          </h2>
          <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
            <button
              onClick={handleICS}
              style={{
                padding: "10px 16px",
                background: "transparent",
                color: t.ink,
                border: `1px solid ${t.ink}55`,
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.1em",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
              aria-label={tr("calendar.add")}
            >
              <span aria-hidden>📅</span>
              <span>{tr("calendar.add")}</span>
            </button>
          </div>
        </div>
        <div>
          {items.map(([time, ev], i) => (
            <div
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "110px 1fr",
                gap: 32,
                padding: "24px 0",
                borderBottom: i < items.length - 1 ? `1px solid ${t.ink}22` : "none",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  fontFamily: t.font || "var(--font-display)",
                  fontStyle: "italic",
                  fontSize: 32,
                  color: t.accent,
                }}
              >
                {time}
              </span>
              <span style={{ fontFamily: "var(--font-body)", fontSize: 18 }}>{ev}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── MAP ───────────────────────────────────────────────────────────────

interface MapSectionProps {
  t: Template;
  isDark: boolean;
  addressLine: string;
}

export function MapSection({ t, isDark, addressLine }: MapSectionProps) {
  const tr = useT();
  const [iframeLoaded, setIframeLoaded] = useState(false);
  // Template doesn't carry lat/lng yet — always fall back to Yandex's
  // ?text= query (server-side geocode). When the backend grows a geocoding
  // pass, swap to ll=lng,lat&z=15&pt=lng,lat,pm2rdm.
  const yandexEmbedSrc = `https://yandex.com/map-widget/v1/?text=${encodeURIComponent(addressLine)}&z=15`;
  const yandexOpenHref = `https://yandex.com/maps/?text=${encodeURIComponent(addressLine)}`;

  return (
    <section
      style={{
        padding: "100px 48px",
        borderTop: `1px solid ${t.ink}22`,
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.4em",
          color: t.accent,
          marginBottom: 16,
        }}
      >
        {tr("sections.map.eyebrow")}
      </div>
      <h2
        style={{
          fontFamily: t.font || "var(--font-display)",
          fontSize: 48,
          fontStyle: "italic",
          fontWeight: 400,
          marginBottom: 24,
        }}
      >
        {tr("sections.map.title")}
      </h2>
      <p style={{ fontFamily: "var(--font-body)", fontSize: 18, marginBottom: 40 }}>{addressLine}</p>
      <div
        style={{
          maxWidth: 720,
          margin: "0 auto",
          position: "relative",
          border: `1px solid ${t.accent}`,
          background: `${t.ink}11`,
        }}
      >
        {!iframeLoaded && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.3em",
              color: t.accent,
              zIndex: 1,
              pointerEvents: "none",
            }}
          >
            {tr("sections.map.loading")}
          </div>
        )}
        <iframe
          title="Yandex Maps"
          src={yandexEmbedSrc}
          loading="lazy"
          onLoad={() => setIframeLoaded(true)}
          allow="geolocation"
          style={{
            display: "block",
            width: "100%",
            height: "var(--map-h, 320px)",
            border: 0,
          }}
        />
        {/* Inline media query via CSS var override */}
        <style>{`@media (min-width: 768px){[data-takmap]{--map-h:480px}}`}</style>
        <span data-takmap style={{ display: "none" }} />
      </div>
      <a
        href={yandexOpenHref}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: "inline-block",
          marginTop: 24,
          padding: "14px 28px",
          background: t.accent,
          color: isDark ? t.bg : "#fff",
          border: `1px solid ${t.ink}`,
          fontFamily: "var(--font-ui)",
          fontSize: 13,
          fontWeight: 600,
          textDecoration: "none",
          cursor: "pointer",
          boxShadow: `4px 5px 0 -1px ${t.ink}`,
        }}
      >
        {tr("sections.map.open")}
      </a>
    </section>
  );
}

// ─── RSVP ──────────────────────────────────────────────────────────────

interface RsvpSectionProps {
  t: Template;
  isDark: boolean;
  rsvp: string | null;
  setRsvp: (v: string | null) => void;
  guests: number;
  setGuests: (n: number) => void;
  guestName: string;
  setGuestName: (s: string) => void;
  label?: string;
  /** When provided, RSVP clicks POST to backend optimistically. */
  slug?: string;
}

export function RsvpSection({
  t,
  isDark,
  rsvp,
  setRsvp,
  guests,
  setGuests,
  guestName,
  setGuestName,
  label,
  slug,
}: RsvpSectionProps) {
  const tr = useT();
  const labelText = label ?? tr("rsvp.title");
  // t.date is dd.mm.yyyy when present but may be empty for drafts/demo.
  // Guard against undefined and clamp the month so an invalid date can't
  // produce `sections.rsvp.month.NaN` (which would render the raw key).
  const dateParts = (t.date || "").split(".");
  const dayPart = dateParts[0] || "";
  const monthPart = dateParts[1] || "";
  const monthNum = parseInt(monthPart, 10);
  const monthIdx = Number.isFinite(monthNum) && monthNum >= 1 && monthNum <= 12 ? monthNum - 1 : 0;
  const monthLabel = tr(`sections.rsvp.month.${monthIdx + 1}`);

  const [feedback, setFeedback] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  const respond = (next: "yes" | "no" | "maybe") => {
    const previous = rsvp;
    setRsvp(next); // optimistic
    setFeedback(null);
    if (slug && guestName) {
      postRSVP(slug, {
        name: guestName,
        rsvp: next,
        party_size: guests,
      })
        .then(() => setFeedback({ kind: "ok", msg: tr("sections.rsvp.thanks") }))
        .catch(() => {
          setRsvp(previous);
          setFeedback({ kind: "err", msg: tr("sections.rsvp.err") });
        });
    }
  };

  return (
    <section
      id="rsvp"
      data-section="rsvp"
      style={{
        padding: "120px 48px",
        borderTop: `1px solid ${t.ink}22`,
        background: isDark ? `${t.ink}33` : `${t.accent}10`,
        textAlign: "center",
        scrollMarginTop: 24,
      }}
    >
      <div style={{ maxWidth: 560, margin: "0 auto" }}>
        <div style={{ color: t.accent, marginBottom: 24, display: "flex", justifyContent: "center" }}>
          <Ornaments.Diamond size={20} color={t.accent} />
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: t.accent,
            marginBottom: 20,
          }}
        >
          {tr("sections.rsvp.eyebrow")}
        </div>
        <h2
          style={{
            fontFamily: t.font || "var(--font-display)",
            fontSize: "clamp(40px, 5vw, 60px)",
            fontStyle: "italic",
            fontWeight: 400,
            marginBottom: 24,
          }}
        >
          {labelText}
        </h2>
        <p
          style={{
            fontFamily: "var(--font-body)",
            fontSize: 17,
            marginBottom: 40,
            color: `${t.ink}cc`,
          }}
        >
          {tr("sections.rsvp.deadline").replace("{day}", dayPart).replace("{month}", monthLabel)}
        </p>
        {rsvp === "yes" ? (
          <div
            style={{
              padding: 32,
              border: `1px solid ${t.accent}`,
              marginBottom: 24,
              background: t.bg,
              animation: "fadeUp .5s ease",
            }}
          >
            <div
              style={{
                fontFamily: t.font || "var(--font-display)",
                fontSize: 32,
                fontStyle: "italic",
                color: t.accent,
                marginBottom: 24,
              }}
            >
              {tr("sections.rsvp.thanks_short")}
            </div>
            <input
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder={tr("sections.rsvp.your_name")}
              style={{
                width: "100%",
                padding: 14,
                marginBottom: 16,
                border: `1px solid ${t.ink}33`,
                background: "transparent",
                color: t.ink,
                fontFamily: "var(--font-body)",
                fontSize: 15,
                outline: "none",
              }}
            />
            <div
              style={{
                display: "flex",
                gap: 12,
                justifyContent: "center",
                alignItems: "center",
                fontFamily: "var(--font-ui)",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <span>{tr("sections.rsvp.guest_count")}</span>
              <button
                onClick={() => setGuests(Math.max(1, guests - 1))}
                style={{
                  width: 36,
                  height: 36,
                  border: `1px solid ${t.accent}`,
                  background: "transparent",
                  color: t.ink,
                  cursor: "pointer",
                  fontSize: 16,
                }}
              >
                −
              </button>
              <span
                style={{
                  fontFamily: t.font || "var(--font-display)",
                  fontSize: 26,
                  fontStyle: "italic",
                  minWidth: 32,
                  color: t.accent,
                }}
              >
                {guests}
              </span>
              <button
                onClick={() => setGuests(guests + 1)}
                style={{
                  width: 36,
                  height: 36,
                  border: `1px solid ${t.accent}`,
                  background: t.accent,
                  color: isDark ? t.bg : "#fff",
                  cursor: "pointer",
                  fontSize: 16,
                }}
              >
                +
              </button>
            </div>
          </div>
        ) : rsvp === "no" ? (
          <div
            style={{
              padding: 32,
              border: `1px solid ${t.ink}33`,
              marginBottom: 24,
              background: t.bg,
            }}
          >
            <div
              style={{
                fontFamily: t.font || "var(--font-display)",
                fontSize: 26,
                fontStyle: "italic",
                marginBottom: 12,
              }}
            >
              {tr("sections.rsvp.regret_title")}
            </div>
            <p
              style={{
                fontFamily: "var(--font-body)",
                fontSize: 15,
                color: `${t.ink}cc`,
              }}
            >
              {tr("sections.rsvp.regret_body")}
            </p>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              data-rsvp="yes"
              onClick={() => respond("yes")}
              style={{
                padding: "18px 36px",
                background: t.accent,
                color: isDark ? t.bg : "#fff",
                border: `1px solid ${t.ink}`,
                fontFamily: "var(--font-ui)",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: `5px 6px 0 -1px ${t.ink}`,
              }}
            >
              {tr("rsvp.yes")}
            </button>
            <button
              data-rsvp="no"
              onClick={() => respond("no")}
              style={{
                padding: "18px 36px",
                background: "transparent",
                color: t.ink,
                border: `1px solid ${t.ink}`,
                fontFamily: "var(--font-ui)",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: `5px 6px 0 -1px ${t.accent}`,
              }}
            >
              {tr("rsvp.no")}
            </button>
          </div>
        )}
        {feedback && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 20,
              padding: "10px 16px",
              fontFamily: "var(--font-ui)",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.05em",
              color: feedback.kind === "ok" ? t.accent : "#b00020",
              border: `1px solid ${feedback.kind === "ok" ? t.accent : "#b00020"}55`,
              background: t.bg,
              display: "inline-block",
            }}
          >
            {feedback.msg}
          </div>
        )}
      </div>
    </section>
  );
}

// ─── GUESTBOOK ─────────────────────────────────────────────────────────

export interface Wish {
  name: string;
  text: string;
  time: string;
}

interface GuestbookSectionProps {
  t: Template;
  isDark: boolean;
  wishes: Wish[];
  setWishes: Dispatch<SetStateAction<Wish[]>>;
  guestName: string;
  setGuestName: (s: string) => void;
  wish: string;
  setWish: (s: string) => void;
  /** When provided, submissions POST to backend. */
  slug?: string;
}

export function GuestbookSection({
  t,
  isDark,
  wishes,
  setWishes,
  guestName,
  setGuestName,
  wish,
  setWish,
  slug,
}: GuestbookSectionProps) {
  const tr = useT();
  const [feedback, setFeedback] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);
  const [sending, setSending] = useState(false);

  // Pull the canonical wish list from the public guestbook endpoint so
  // every visitor sees every wish — not just the ones they personally
  // submitted from this device. We also re-fetch on tab focus so a
  // freshly-deleted wish (owner moderation) stops showing up without
  // forcing a hard refresh.
  useEffect(() => {
    if (!slug) return;
    let alive = true;
    const pull = () => {
      getPublicGuestbook(slug)
        .then((arr) => {
          if (!alive) return;
          const fromServer: Wish[] = arr.map((w) => {
            const dt = new Date(w.created_at);
            const diff = Date.now() - dt.getTime();
            const time =
              diff < 60_000 ? "hozirgina"
              : diff < 3600_000 ? `${Math.floor(diff / 60_000)} daqiqa oldin`
              : diff < 86400_000 ? `${Math.floor(diff / 3600_000)} soat oldin`
              : diff < 86400_000 * 2 ? "kecha"
              : `${Math.floor(diff / 86400_000)} kun oldin`;
            return { name: w.name, text: w.message, time };
          });
          // Merge with any optimistic entries that haven't been observed
          // yet (deduped by name+text). Server view wins for ordering.
          setWishes((prev) => {
            const seen = new Set<string>();
            const out: Wish[] = [];
            for (const w of fromServer) {
              const k = w.name + "|" + w.text;
              if (seen.has(k)) continue;
              seen.add(k);
              out.push(w);
            }
            for (const w of prev) {
              const k = w.name + "|" + w.text;
              if (seen.has(k)) continue;
              seen.add(k);
              out.push(w);
            }
            return out;
          });
        })
        .catch(() => { /* ignore — keeps the optimistic list usable offline */ });
    };
    pull();
    const onVis = () => { if (document.visibilityState === "visible") pull(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [slug, setWishes]);

  const submit = () => {
    if (!guestName || !wish || sending) return;
    const optimistic: Wish = { name: guestName, text: wish, time: "hozirgina" };
    setWishes([optimistic, ...wishes]);
    setWish("");
    setFeedback(null);

    if (!slug) return;
    setSending(true);
    postGuestbook(slug, { name: optimistic.name, message: optimistic.text })
      .then(() => {
        setFeedback({ kind: "ok", msg: tr("sections.guestbook.ok") });
      })
      .catch(() => {
        // Revert optimistic add (remove first matching entry).
        setWishes((prev) => {
          const i = prev.findIndex(
            (w) => w.name === optimistic.name && w.text === optimistic.text && w.time === "hozirgina",
          );
          if (i === -1) return prev;
          const next = prev.slice();
          next.splice(i, 1);
          return next;
        });
        setFeedback({ kind: "err", msg: tr("sections.guestbook.err") });
      })
      .finally(() => setSending(false));
  };

  const disabled = !guestName || !wish || sending;
  // Closed = 24h have passed since the wedding start. Reading the wish
  // list stays open; the submit form is replaced by a calm "closed" panel.
  const closed = isPastEvent(t.date, t.time);
  return (
    <section className="cv-auto" style={{ padding: "120px 48px", borderTop: `1px solid ${t.ink}22` }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <div style={{ fontFamily: "var(--font-hand)", fontSize: 32, color: t.accent }}>guest book</div>
          <h2
            style={{
              fontFamily: t.font || "var(--font-display)",
              fontSize: 48,
              fontStyle: "italic",
              fontWeight: 400,
              marginTop: 8,
            }}
          >
            {tr("sections.guestbook.title")}
          </h2>
        </div>
        {closed ? (
          <div
            role="status"
            aria-live="polite"
            style={{
              padding: "40px 28px",
              border: `1px solid ${t.accent}55`,
              background: `${t.accent}08`,
              marginBottom: 40,
              textAlign: "center",
              position: "relative",
            }}
          >
            {/* Hairline ornament rule with a centered diamond — same
                editorial motif as the rest of the heirloom system. */}
            <div
              aria-hidden
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
                marginBottom: 22,
                color: t.accent,
              }}
            >
              <span
                style={{
                  flex: "0 0 64px",
                  height: 1,
                  background: `linear-gradient(to right, transparent, ${t.accent})`,
                }}
              />
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M8 1 L11 8 L8 15 L5 8 Z" stroke={t.accent} strokeWidth="0.8" />
                <circle cx="8" cy="8" r="1.2" fill={t.accent} />
              </svg>
              <span
                style={{
                  flex: "0 0 64px",
                  height: 1,
                  background: `linear-gradient(to left, transparent, ${t.accent})`,
                }}
              />
            </div>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.32em",
                textTransform: "uppercase",
                color: t.accent,
                marginBottom: 12,
              }}
            >
              {tr("sections.guestbook.closed.eyebrow")}
            </div>
            <div
              style={{
                fontFamily: t.font || "var(--font-display)",
                fontSize: 28,
                fontStyle: "italic",
                fontWeight: 400,
                lineHeight: 1.15,
                color: t.ink,
                marginBottom: 12,
              }}
            >
              {tr("sections.guestbook.closed.title")}
            </div>
            <p
              style={{
                fontFamily: "var(--font-body)",
                fontStyle: "italic",
                fontSize: 16,
                lineHeight: 1.55,
                color: `${t.ink}cc`,
                maxWidth: 460,
                margin: "0 auto",
              }}
            >
              {tr("sections.guestbook.closed.body")}
            </p>
            {wishes.length > 0 && (
              <div
                style={{
                  marginTop: 22,
                  fontFamily: "var(--font-ui)",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: `${t.ink}88`,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {tr("sections.guestbook.closed.count").replace("{count}", String(wishes.length))}
              </div>
            )}
          </div>
        ) : (
        <div
          style={{
            padding: 24,
            border: `1px solid ${t.accent}`,
            background: t.bg,
            marginBottom: 40,
          }}
        >
          <input
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            placeholder={tr("sections.guestbook.name_placeholder")}
            style={{
              width: "100%",
              padding: 12,
              marginBottom: 12,
              border: `1px solid ${t.ink}22`,
              background: "transparent",
              color: t.ink,
              fontFamily: "var(--font-body)",
              fontSize: 15,
              outline: "none",
            }}
          />
          <textarea
            value={wish}
            onChange={(e) => setWish(e.target.value)}
            placeholder={tr("sections.guestbook.placeholder")}
            style={{
              width: "100%",
              padding: 12,
              minHeight: 80,
              border: `1px solid ${t.ink}22`,
              background: "transparent",
              color: t.ink,
              fontFamily: "var(--font-body)",
              fontSize: 15,
              outline: "none",
              resize: "vertical",
            }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 16, flexWrap: "wrap" }}>
            <button
              onClick={submit}
              disabled={disabled}
              style={{
                padding: "14px 28px",
                background: t.accent,
                color: isDark ? t.bg : "#fff",
                border: `1px solid ${t.ink}`,
                fontFamily: "var(--font-ui)",
                fontSize: 13,
                fontWeight: 700,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.5 : 1,
                boxShadow: `4px 5px 0 -1px ${t.ink}`,
              }}
            >
              {sending ? tr("sections.guestbook.sending") : tr("sections.guestbook.send")}
            </button>
            {feedback && (
              <span
                role="status"
                aria-live="polite"
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 12,
                  fontWeight: 600,
                  color: feedback.kind === "ok" ? t.accent : "#b00020",
                }}
              >
                {feedback.msg}
              </span>
            )}
          </div>
        </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {wishes.map((w, i) => (
            <div
              key={i}
              style={{
                padding: 24,
                borderLeft: `2px solid ${t.accent}`,
                background: i === 0 && w.time === "hozirgina" ? `${t.accent}15` : "transparent",
                animation: i === 0 && w.time === "hozirgina" ? "fadeUp .4s ease" : "none",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  marginBottom: 8,
                }}
              >
                <span
                  style={{
                    fontFamily: t.font || "var(--font-display)",
                    fontStyle: "italic",
                    fontSize: 22,
                    color: t.accent,
                  }}
                >
                  {w.name}
                </span>
                <span
                  style={{
                    fontFamily: "var(--font-ui)",
                    fontSize: 12,
                    fontWeight: 600,
                    color: `${t.ink}88`,
                  }}
                >
                  {w.time}
                </span>
              </div>
              <p
                style={{
                  fontFamily: "var(--font-body)",
                  fontStyle: "italic",
                  fontSize: 16,
                  lineHeight: 1.6,
                  color: `${t.ink}dd`,
                }}
              >
                "{w.text}"
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── SHARE ─────────────────────────────────────────────────────────────

interface ShareSectionProps {
  t: Template;
}

export function ShareSection({ t }: ShareSectionProps) {
  const tr = useT();
  // Live share — every button does something on tap.
  // - Telegram / WhatsApp open the platform share intent (deep link → app on phone)
  // - "Native" uses Web Share API on iOS/Android to bring up the OS share sheet
  // - "Copy" copies the URL with a confirmation tick
  const [copied, setCopied] = useState(false);
  const [canShare] = useState(
    typeof navigator !== "undefined" && typeof navigator.share === "function",
  );
  // Share the canonical guest URL — strip any query params the local viewer
  // had on screen (e.g. ?names=...&date=... that the detail-page preview
  // injects). The recipient should land on the published slug directly,
  // and the backend will serve the right invitation. Falls back to current
  // location only if origin/path detection somehow fails.
  const url =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}`
      : "";
  const text = `${t.names} · ${t.date} — ${t.venue}`;

  const intents: Array<[label: string, code: string, action: () => void]> = [
    [
      tr("share.native"),
      "↗",
      () => {
        if (canShare) {
          navigator.share({ title: t.names, text, url }).catch(() => {});
        } else {
          window.open(url, "_blank");
        }
      },
    ],
    [
      tr("share.telegram"),
      "TG",
      () => {
        const u = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
        window.open(u, "_blank", "noopener");
      },
    ],
    [
      tr("share.whatsapp"),
      "WA",
      () => {
        const u = `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`;
        window.open(u, "_blank", "noopener");
      },
    ],
    [
      copied ? tr("share.copied") : tr("common.copy"),
      copied ? "✓" : "COPY",
      async () => {
        try {
          await navigator.clipboard.writeText(url);
          setCopied(true);
          setTimeout(() => setCopied(false), 1800);
        } catch {
          /* ignored — older browsers */
        }
      },
    ],
  ];
  // On desktop, the OS share sheet usually doesn't exist — drop the Native button.
  const visible = canShare ? intents : intents.slice(1);

  return (
    <section
      id="share"
      data-section="share"
      style={{
        padding: "80px 48px",
        borderTop: `1px solid ${t.ink}22`,
        textAlign: "center",
        scrollMarginTop: 24,
      }}
    >
      <h3
        style={{
          fontFamily: t.font || "var(--font-display)",
          fontSize: 32,
          fontStyle: "italic",
          fontWeight: 400,
          marginBottom: 24,
        }}
      >
        {tr("share.title")}
      </h3>
      <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
        {visible.map(([n, c, fn]) => (
          <button
            key={n}
            onClick={fn}
            style={{
              padding: "14px 22px",
              background: "transparent",
              color: t.ink,
              border: `1px solid ${t.accent}`,
              fontFamily: "var(--font-ui)",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              gap: 10,
              alignItems: "center",
              minHeight: 48,
            }}
          >
            <span style={{ color: t.accent }}>{c}</span>
            {n}
          </button>
        ))}
      </div>
    </section>
  );
}

// ─── FOOTER ────────────────────────────────────────────────────────────

interface GuestFooterProps {
  t: Template;
}

export function GuestFooter({ t }: GuestFooterProps) {
  const tr = useT();
  return (
    <section style={{ padding: "60px 48px", textAlign: "center", borderTop: `1px solid ${t.ink}22` }}>
      <div style={{ color: t.accent, marginBottom: 16, display: "flex", justifyContent: "center" }}>
        <Ornaments.Flourish width={120} color={t.accent} />
      </div>
      <div
        style={{
          fontFamily: "var(--font-hand)",
          fontSize: 32,
          color: t.accent,
          marginBottom: 8,
        }}
      >
        see you soon
      </div>
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 12,
          fontWeight: 600,
          color: `${t.ink}88`,
        }}
      >
        {t.names} · {t.date}
      </div>
      <div
        style={{
          marginTop: 32,
          fontFamily: "var(--font-ui)",
          fontSize: 11,
          fontWeight: 600,
          color: `${t.ink}66`,
        }}
      >
        {tr("sections.footer.signature")}
      </div>
    </section>
  );
}
